import React, {useState} from "react";
import {QualityAction, QualityActionRestControllerApiFactory, QualityFinding, QualityFindingRestControllerApiFactory} from "arteco-api-client-ts";
import {FormContent} from "../../../../components/form/form_inputs/form_buttons";
import {FormInput} from "../../../../components/form/form_inputs/form_input";
import FormSelect from "../../../../components/form/form_inputs/form_select";
import FormAutocomplete from "../../../../components/form/form_inputs/form_autocomplete";
import FormToggle from "../../../../components/form/form_inputs/form_toggle";
import FormFile from "../../../../components/form/form_inputs/form_file";
import {Box, Button, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography} from "@mui/material";
import {useForm, UseFormProps} from "../../../../components/form/form_model";
import {useApiManager} from "../../../../components/utils/api";
import {types} from "../../../../components/form/types";
import {useNavigate} from "react-router-dom";

/** Ficha de un hallazgo del sistema de gestión y sus acciones (#13718). */
export const QualityFindingEditPage = () => {
	const apiManager = useApiManager();
	const navigate = useNavigate();
	const ctrl = apiManager.factory(QualityFindingRestControllerApiFactory);
	const actionCtrl = apiManager.factory(QualityActionRestControllerApiFactory);
	const [actions, setActions] = useState<QualityAction[]>([]);

	const form = useForm({
		fetcher: (id) => ctrl.getQualityFinding(id),
		updater: (bean) => ctrl.saveQualityFinding(bean),
		remover: (id) => ctrl.deleteOrDismissQualityFinding(id),
		factory: () => ({
			status: "ABIERTA",
			detectedDate: new Date().toISOString().substring(0, 10),
		} as QualityFinding),
		postLoad: (bean: QualityFinding) => {
			if (bean.id) {
				actionCtrl.pageQualityAction(0, 100, undefined, bean.id, undefined, false)
				.then(resp => setActions((resp.data?.content || []) as QualityAction[]));
			}
		},
	} as UseFormProps<QualityFinding>);

	const tipo = form.value("type");

	return (
		<FormContent form={form} title={"Hallazgo del sistema de gestión"}>
			<Grid container spacing={2}>
				<Grid item xs={12} sm={3}>
					<FormSelect field={"type"} label={"Tipo"} type={types.findingTypes}/>
				</Grid>
				<Grid item xs={12} sm={9}>
					<FormInput field={"title"} label={"Título"}/>
				</Grid>
				<Grid item xs={12} sm={3}>
					<FormSelect field={"origin"} label={"Origen"} type={types.findingOrigins}/>
				</Grid>
				<Grid item xs={12} sm={3}>
					<FormSelect field={"severity"} label={"Gravedad"} type={types.findingSeverities}/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<FormInput field={"requirement"} label={"Requisito afectado"}
							   helperText={"Cláusula o medida, p. ej. «ISO 27001 A.5.24 · ENS op.exp.7»"}/>
				</Grid>
				<Grid item xs={12} sm={3}>
					<FormInput field={"detectedDate"} type={"date"} label={"Detectado el"}/>
				</Grid>
				<Grid item xs={12} sm={3}>
					<FormAutocomplete field={"detectedBy"} label={"Detectado por"} type={types.people}/>
				</Grid>
				<Grid item xs={12} sm={3}>
					<FormAutocomplete field={"responsible"} label={"Responsable"} type={types.people}/>
				</Grid>
				<Grid item xs={12} sm={3}>
					<FormInput field={"dueDate"} type={"date"} label={"Plazo"}/>
				</Grid>
				{tipo === "RECLAMACION" && (
					<Grid item xs={12} sm={6}>
						<FormAutocomplete field={"customer"} label={"Cliente"} type={types.customers}/>
					</Grid>
				)}
			</Grid>

			<FormInput field={"description"} label={"Qué pasó"} rows={4}/>
			<FormInput field={"rootCause"} label={"Análisis de causa"} rows={3}
					   helperText={"Obligatorio para cerrar una no conformidad o un incidente"}/>

			{tipo === "INCIDENTE_SEGURIDAD" && (
				<Grid container spacing={2}>
					<Grid item xs={12} sm={4}>
						<FormToggle field={"dataBreach"} label={"Afecta a datos personales"}/>
					</Grid>
					<Grid item xs={12} sm={4}>
						<FormInput field={"authorityNotifiedDate"} type={"date"} label={"Notificado a la autoridad el"}
								   helperText={"AEPD en 72 h si es una brecha; CCN-CERT si aplica el ENS"}/>
					</Grid>
					<Grid item xs={12}>
						{/* #14010: con la fecha de notificación o con este motivo dejan de llegar los avisos de 24, 48 y 72 h. */}
						<FormInput field={"notificationDecision"} label={"Decisión sobre notificar"} rows={2}
								   helperText={"Si no procede notificar, por qué (por ejemplo, no afecta a datos personales); si se notificó, cómo"}/>
					</Grid>
				</Grid>
			)}

			<Grid container spacing={2}>
				<Grid item xs={12} sm={3}>
					<FormSelect field={"status"} label={"Estado"} type={types.findingStatuses}/>
				</Grid>
				<Grid item xs={12} sm={3}>
					<FormToggle field={"effective"} label={"Acciones eficaces"}/>
				</Grid>
				<Grid item xs={12} sm={3}>
					<FormInput field={"effectivenessDate"} type={"date"} label={"Eficacia verificada el"}/>
				</Grid>
				<Grid item xs={12} sm={3}>
					<FormInput field={"closedDate"} type={"date"} label={"Cerrada el"} disabled/>
				</Grid>
			</Grid>
			<FormInput field={"effectivenessNotes"} label={"Cómo se verificó la eficacia"} rows={2}/>

			<FormFile field={"attachment"} label={"Evidencia"}
					  onDownload={() => apiManager.executeRaw(ctrl.viewQualityFinding(form.getIdOrErr()))}/>

			{form.getId() && (
				<>
					<Typography variant={"h6"} sx={{mt: 3, mb: 1}}>Acciones</Typography>
					<Button variant="outlined" size="small" sx={{mb: 1}}
							onClick={() => navigate(`/app/admin/compliance/action/new?findingId=${form.getId()}`)}>
						Añadir acción
					</Button>
					<Box sx={{overflowX: "auto", minWidth: 0}}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Tipo</TableCell><TableCell>Acción</TableCell><TableCell>Responsable</TableCell>
									<TableCell>Plazo</TableCell><TableCell>Hecha</TableCell><TableCell>Estado</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{actions.map(a => (
									<TableRow key={a.id} hover sx={{cursor: "pointer"}}
											  onClick={() => navigate(`/app/admin/compliance/action/${a.id}?findingId=${form.getId()}`)}>
										<TableCell>{a.type}</TableCell>
										<TableCell>{a.description}</TableCell>
										<TableCell>{(a.responsible as any)?.surnamesAndName ?? "-"}</TableCell>
										<TableCell>{a.dueDate ?? "-"}</TableCell>
										<TableCell>{a.doneDate ?? "-"}</TableCell>
										<TableCell>{a.status}</TableCell>
									</TableRow>
								))}
								{actions.length === 0 && (
									<TableRow><TableCell colSpan={6}>Sin acciones todavía.</TableCell></TableRow>
								)}
							</TableBody>
						</Table>
					</Box>
				</>
			)}
		</FormContent>
	);
};
